import { StoredProcedureScope } from '@cloud-native/workflows-engine'
import { FeatureFlag, useFeatureFlag } from 'app/ui/useFeatureFlag'
import { Workflow, WorkflowListItem } from 'features/workflows/types'
import { getMcpToolVariables } from './variables'
import { formatWorkflowUrl, formatWorkflowPayload } from './formatCodeSnippets'
import { isProcedureOutdated } from '../service/common/utils'
import { sanitizeNameToSqlIdentifier } from 'features/builder/utils/sqlParameterUtils'

export function getMcpToolName(workflow: Workflow | WorkflowListItem): string {
  return sanitizeNameToSqlIdentifier(workflow.config.title?.toLowerCase() || '')
}

export function useMcpToolEnabled(workflow: Workflow | WorkflowListItem) {
  const isFeatureFlagEnabled = useFeatureFlag(FeatureFlag.McpServerUrlV1)
  const isProcedureCreated = Boolean(workflow?.config?.mcpTool.procedure?.call)
  return isFeatureFlagEnabled && isProcedureCreated
}

export function isMcpToolOutdated(workflow: Workflow) {
  const mcpTool = workflow?.config?.mcpTool
  const mcpToolDraft = mcpTool?.draft
  const isEnabled = !!mcpTool?.enabled
  const isNameOutdated = mcpTool?.name !== getMcpToolName(workflow)
  const isDescriptionOutdated = mcpTool?.description !== mcpToolDraft?.description
  const isOutputOutdated = mcpTool?.output?.description !== mcpToolDraft?.output?.description
  const isInputsOutdated = mcpTool?.inputs?.some(
    (input) => input.description !== mcpToolDraft?.inputs?.[input.name]?.description
  )
  return (
    isEnabled &&
    (isProcedureOutdated(workflow, StoredProcedureScope.McpTool) ||
      isNameOutdated ||
      isDescriptionOutdated ||
      isOutputOutdated ||
      isInputsOutdated)
  )
}

export function isMcpToolFormValid(workflow: Workflow) {
  return (
    Boolean(workflow?.config.mcpTool.draft?.description?.trim()) &&
    Boolean(workflow?.config.mcpTool.draft?.output?.description?.trim()) &&
    getMcpToolVariables(workflow?.config.variables).every((variable) =>
      Boolean(workflow?.config.mcpTool.draft?.inputs?.[variable.name]?.description?.trim())
    )
  )
}

export function generateMcpToolOutputPayload(workflow: Workflow, isWorkflowSync: boolean) {
  if (workflow?.config?.mcpTool.procedure?.call) {
    const formattedPayload = formatWorkflowPayload(workflow, isWorkflowSync)
    return {
      method: 'POST' as const,
      endpoint: formatWorkflowUrl(workflow, isWorkflowSync),
      payload: {
        q: (formattedPayload.q as string) || '',
        query: (formattedPayload.query as string) || '',
        queryParameters: (formattedPayload.queryParameters as Record<string, string>) || {}
      }
    }
  }
  return {
    method: 'GET' as const,
    endpoint: '',
    payload: {
      q: '',
      query: '',
      queryParameters: {}
    }
  }
}
